<template>
  <div class="w-full h-full space-y-4">
    <!-- Summary -->
    <Card>
      <CardHeader>
        <CardTitle>End user</CardTitle>
        <CardDescription class="break-all">{{ userId }}</CardDescription>
      </CardHeader>
      <CardContent>
        <div class="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
          <div>
            <p class="text-xs uppercase tracking-wide text-muted-foreground mb-1">Enrolled on</p>
            <p class="font-medium">{{ user?.createdOn ? niceDateTime(user.createdOn) : '—' }}</p>
          </div>
          <div>
            <p class="text-xs uppercase tracking-wide text-muted-foreground mb-1">Segments</p>
            <div class="flex flex-wrap gap-1">
              <!-- customerTiers is a set of segment ids; segmentName resolves each to its
                   name so the pills do not show raw ids. -->
              <Badge v-for="(t, i) in (user?.customerTiers || [])" :key="i" variant="secondary">
                {{ segmentName(t) }}
              </Badge>
              <span v-if="!user?.customerTiers?.length" class="text-muted-foreground">—</span>
            </div>
          </div>
          <div>
            <p class="text-xs uppercase tracking-wide text-muted-foreground mb-1">Open support tickets</p>
            <p class="font-medium tabular-nums">{{ ticketsTotal ?? '—' }}</p>
          </div>
        </div>
      </CardContent>
    </Card>

    <Card>
      <CardContent class="pt-6">
        <Tabs v-model="activeTab" @update:model-value="onTabChange">
          <TabsList class="mb-4 flex-wrap">
            <TabsTrigger value="cashback">Cashback</TabsTrigger>
            <TabsTrigger value="missed-opportunities">Boogi intelligence</TabsTrigger>
            <TabsTrigger value="transactions">Transactions</TabsTrigger>
            <TabsTrigger value="advertising">Advertising events</TabsTrigger>
            <TabsTrigger value="tickets">Support tickets</TabsTrigger>
          </TabsList>

          <!-- Cashback -->
          <TabsContent value="cashback">
            <DataTable :headers="offerHeaders" :items="offers" :loading="loadingOffers" :error="errorOffers"
              @retry="getOffers(offerOptions.page - 1, offerOptions.rowsPerPage)" v-model:server-options="offerOptions"
              :server-items-length="offersTotal">
              <template #item-offer="cb">
                <span v-if="cashbackOfferName(cb)" class="text-sm font-medium truncate"
                  :title="cashbackOfferName(cb)">{{ cashbackOfferName(cb) }}</span>
                <span v-else class="text-muted-foreground">—</span>
              </template>
              <template #item-retailer="cb">
                <span v-if="cashbackRetailerName(cb)" class="text-sm font-medium truncate"
                  :title="cashbackRetailerName(cb)">{{ cashbackRetailerName(cb) }}</span>
                <span v-else class="text-muted-foreground">—</span>
              </template>
              <template #item-cashback="cb">
                <span v-if="cb.payoutData && cb.payoutData.endUserCashbackReceivable != null"
                  class="text-sm tabular-nums whitespace-nowrap">£{{
                    Number(cb.payoutData.endUserCashbackReceivable).toFixed(2) }}</span>
                <span v-else class="text-muted-foreground">—</span>
              </template>
              <template #item-createdOn="{ createdOn }">
                <span class="text-sm text-muted-foreground whitespace-nowrap">{{ createdOn ? niceDateTime(createdOn) :
                  '—' }}</span>
              </template>
              <template #item-status="{ status }">
                <Badge :variant="cashbackStatusVariant(status)">{{ status }}</Badge>
              </template>
            </DataTable>
          </TabsContent>

          <!-- Transactions -->
          <TabsContent value="transactions">
            <DataTable :headers="txHeaders" :items="transactions" :loading="loadingTx" :error="errorTx"
              @retry="getTransactions(txOptions.page - 1, txOptions.rowsPerPage)" v-model:server-options="txOptions"
              :server-items-length="txTotal">
              <template #item-description="tx">
                <span class="text-sm font-medium truncate" :title="txDescription(tx)">{{ txDescription(tx) }}</span>
              </template>
              <template #item-amount="tx">
                <span class="text-sm tabular-nums whitespace-nowrap">{{ txAmount(tx) }}</span>
              </template>
              <template #item-bookingDateTime="{ bookingDateTime }">
                <span class="text-sm text-muted-foreground whitespace-nowrap">{{ bookingDateTime ?
                  niceDateTime(bookingDateTime) : '—' }}</span>
              </template>
            </DataTable>
          </TabsContent>

          <!-- Missed cashback opportunities for this end user -->
          <TabsContent value="missed-opportunities">
            <DataTable :headers="recommendationHeaders" :items="recommendations" :loading="loadingRecommendations"
              :error="errorRecommendations"
              @retry="getRecommendations(recommendationOptions.page - 1, recommendationOptions.rowsPerPage)"
              v-model:server-options="recommendationOptions" :server-items-length="recommendationsTotal">
              <template #item-retailer="row">
                <span class="text-sm font-medium">{{ row.programOffer?.retailer?.retailerName ||
                  row.programOffer?.offer?.offerName || '—' }}</span>
              </template>
              <template #item-recommendationType="row">
                <Badge variant="secondary">{{ formatRecommendationType(row.recommendationType) }}</Badge>
              </template>
              <template #item-transactionData="row">
                <span class="text-sm">{{ recommendationTransaction(row.transactionData) }}</span>
              </template>
              <template #item-payoutData="row">
                <span class="text-sm tabular-nums">{{ recommendationCashback(row.payoutData) }}</span>
              </template>
              <template #item-createdOn="{ createdOn }">
                <span class="text-sm text-muted-foreground whitespace-nowrap">{{ createdOn ? niceDateTime(createdOn) :
                  '—' }}</span>
              </template>
            </DataTable>
          </TabsContent>

          <!-- Advertising events -->
          <TabsContent value="advertising">
            <DataTable :headers="adHeaders" :items="adEvents" :loading="loadingAd" :error="errorAd"
              @retry="getAdvertisingEvents(adOptions.page - 1, adOptions.rowsPerPage)"
              v-model:server-options="adOptions" :server-items-length="adTotal">
              <template #item-eventType="event">
                <Badge variant="secondary">{{ event.eventMetaData?.eventType || '—' }}</Badge>
              </template>
              <template #item-eventAction="event">
                <span class="text-sm whitespace-nowrap">{{ event.eventMetaData?.eventAction || '—' }}</span>
              </template>
              <template #item-eventOfferType="event">
                <span class="text-sm whitespace-nowrap">{{ event.eventMetaData?.eventOfferType || '—' }}</span>
              </template>
              <template #item-eventTimestamp="event">
                <span class="text-sm text-muted-foreground whitespace-nowrap">{{ eventWhen(event) }}</span>
              </template>
            </DataTable>
          </TabsContent>

          <!-- Support tickets -->
          <TabsContent value="tickets">
            <DataTable :headers="ticketHeaders" :items="tickets" :loading="loadingTickets" :error="errorTickets"
              @retry="getTickets(ticketOptions.page - 1, ticketOptions.rowsPerPage)"
              v-model:server-options="ticketOptions" :server-items-length="ticketsTotal">
              <template #item-id="{ id }">
                <CopyId v-if="id" :value="id" :max-width="120" />
                <span v-else class="text-muted-foreground">—</span>
              </template>
              <template #item-resolved="{ resolved }">
                <Badge v-if="resolved" variant="success">Resolved</Badge>
                <Badge v-else variant="warning">Open</Badge>
              </template>
              <!-- The ticket carries transactionId, not a description, so the old
                   transactionDescription slot always rendered a dash. -->
              <template #item-transactionId="{ transactionId }">
                <CopyId v-if="transactionId" :value="transactionId" :max-width="140" />
                <span v-else class="text-muted-foreground">—</span>
              </template>
              <template #item-createdOn="{ createdOn }">
                <span class="text-sm text-muted-foreground whitespace-nowrap">{{ createdOn ? niceDateTime(createdOn) :
                  '—' }}</span>
              </template>
              <template #item-action="ticket">
                <div class="flex justify-end items-center gap-1">
                  <Button variant="outline" size="sm" @click="$router.push(`/support/${ticket.id}`)">View
                    ticket</Button>
                </div>
              </template>
            </DataTable>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  </div>
</template>

<script>
import { DateTime } from 'luxon'
import {
  Card, CardHeader, CardTitle, CardDescription, CardContent,
  Badge, Button, CopyId,
  Tabs, TabsList, TabsTrigger, TabsContent,
} from '@boogi/ui'
import { DataTable } from '@boogi/ui'

export default {
  name: 'user-detail',
  components: {
    DataTable,
    Card, CardHeader, CardTitle, CardDescription, CardContent,
    Badge, Button, CopyId,
    Tabs, TabsList, TabsTrigger, TabsContent,
  },
  data() {
    return {
      userId: this.$route.params.userId,
      user: undefined,
      activeTab: 'cashback',

      offers: [],
      loadingOffers: true,
      errorOffers: false,
      offersTotal: undefined,
      offerOptions: { page: 1, rowsPerPage: 10 },
      offerHeaders: [
        { text: 'Offer', value: 'offer' },
        { text: 'Retailer', value: 'retailer' },
        { text: 'Status', value: 'status' },
        { text: 'Cashback', value: 'cashback' },
        { text: 'Earned on', value: 'createdOn' },
      ],

      transactions: [],
      loadingTx: false,
      errorTx: false,
      txTotal: undefined,
      txLoaded: false,
      txOptions: { page: 1, rowsPerPage: 10 },
      txHeaders: [
        { text: 'Description', value: 'description' },
        { text: 'Amount', value: 'amount' },
        { text: 'Date', value: 'bookingDateTime' },
      ],

      recommendations: [],
      loadingRecommendations: false,
      errorRecommendations: false,
      recommendationsTotal: undefined,
      recommendationsLoaded: false,
      recommendationOptions: { page: 1, rowsPerPage: 10 },
      recommendationHeaders: [
        { text: 'Retailer / Offer', value: 'retailer' },
        { text: 'Type', value: 'recommendationType' },
        { text: 'Transaction', value: 'transactionData' },
        { text: 'Potential cashback', value: 'payoutData' },
        { text: 'Identified on', value: 'createdOn' },
      ],

      adEvents: [],
      loadingAd: false,
      errorAd: false,
      adTotal: undefined,
      adLoaded: false,
      adOptions: { page: 1, rowsPerPage: 10 },
      adHeaders: [
        { text: 'Event', value: 'eventType' },
        { text: 'Action', value: 'eventAction' },
        { text: 'Offer type', value: 'eventOfferType' },
        { text: 'When', value: 'eventTimestamp' },
      ],

      tickets: [],
      loadingTickets: true,
      errorTickets: false,
      // segment id -> name, so the summary pills show names rather than ids
      segmentNames: {},
      ticketsTotal: undefined,
      ticketOptions: { page: 1, rowsPerPage: 10 },
      ticketHeaders: [
        { text: 'Ticket id', value: 'id' },
        { text: 'Status', value: 'resolved' },
        { text: 'Transaction id', value: 'transactionId' },
        { text: 'Created on', value: 'createdOn' },
        { text: 'Action', value: 'action' },
      ],
    }
  },
  created() {
    this.getUser()
    this.getSegments()
    this.getOffers(0, 10)
    // Fetch tickets up front so the summary count is populated.
    this.getTickets(0, 10)
  },
  methods: {
    niceDateTime(dateObject) {
      return DateTime.fromMillis(dateObject).toLocaleString(DateTime.DATETIME_MED)
    },
    cashbackOfferName(cb) {
      return cb?.programOffer?.offer?.offerName || cb?.payoutData?.merchant || ''
    },
    cashbackRetailerName(cb) {
      return cb?.programOffer?.retailer?.retailerName || cb?.payoutData?.merchant || ''
    },
    cashbackStatusVariant(status) {
      const s = String(status || '').toUpperCase()
      if (['PAID', 'RECEIVED', 'VERIFIED'].includes(s)) return 'success'
      if (['DECLINED', 'DISQUALIFIED', 'REFUNDED'].includes(s)) return 'destructive'
      if (['CREATED', 'BEING_VERIFIED'].includes(s)) return 'warning'
      return 'secondary'
    },
    txDescription(tx) {
      return (
        tx.enrichedCreditorData?.merchant ||
        tx.unEnrichedCreditorName ||
        tx.remittanceInformationUnstructured ||
        '—'
      )
    },
    txAmount(tx) {
      const a = tx.transactionAmount || tx.standardisedGBPTransactionAmount
      if (!a || a.amount == null) return '—'
      const currency = a.currency ? `${a.currency} ` : ''
      return `${currency}${a.amount}`
    },
    eventWhen(event) {
      const ts = event.eventTimestamp || event.createdOn
      return ts ? this.niceDateTime(ts) : '—'
    },
    onTabChange(tab) {
      this.activeTab = tab
      if (tab === 'transactions' && !this.txLoaded) this.getTransactions(0, this.txOptions.rowsPerPage)
      if (tab === 'missed-opportunities' && !this.recommendationsLoaded) this.getRecommendations(0, this.recommendationOptions.rowsPerPage)
      if (tab === 'advertising' && !this.adLoaded) this.getAdvertisingEvents(0, this.adOptions.rowsPerPage)
    },
    authHeaders() {
      return { 'Content-Type': 'application/json', Authorization: `${localStorage.getItem('token')}` }
    },
    apiBase() {
      return localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL
    },
    async getUser() {
      try {
        const response = await this.$apiFetch(
          `${this.apiBase()}/private/api/client/end-user/${this.userId}`,
          { method: 'GET', headers: this.authHeaders() }
        )
        if (response.ok) this.user = await response.json()
      } catch (error) {
        return
      }
    },
    segmentName(tier) {
      if (tier && typeof tier === 'object') return tier.name || tier.id || '—'
      return this.segmentNames[tier] || tier
    },
    async getSegments() {
      try {
        const response = await this.$apiFetch(
          `${this.apiBase()}/private/api/client/customer-tier?page=0&size=200`,
          { method: 'GET', headers: this.authHeaders() }
        )
        if (response.ok) {
          const object = await response.json()
          const map = {}
          for (const tier of object.content || []) map[tier.id] = tier.name
          this.segmentNames = map
        }
      } catch (e) {
        // Names are cosmetic; fall back to showing the raw id.
      }
    },
    async getTickets(page, size) {
      this.loadingTickets = true
      this.errorTickets = false
      try {
        const response = await this.$apiFetch(
          // resolved=false -> open tickets only (also the API default, pinned here so the
          // "Open support tickets" count cannot drift if that default changes).
          `${this.apiBase()}/private/api/client/support/user/${this.userId}?page=${page}&size=${size}&resolved=false`,
          { method: 'GET', headers: this.authHeaders() }
        )
        if (response.ok) {
          const object = await response.json()
          this.tickets = object.content
          this.ticketsTotal = object.totalElements
        } else {
          this.errorTickets = true
        }
      } catch (e) {
        this.errorTickets = true
      } finally {
        this.loadingTickets = false
      }
    },
    // The user's earned cashback (cashback ledger for this end user), not just
    // the offers assigned to them — CDLX users earn cashback without an assigned offer.
    async getOffers(page, size) {
      this.loadingOffers = true
      this.errorOffers = false
      try {
        const response = await this.$apiFetch(
          `${this.apiBase()}/private/api/client/cashback-ledger/user/${this.userId}?page=${page}&size=${size}`,
          { method: 'GET', headers: this.authHeaders() }
        )
        if (response.ok) {
          const object = await response.json()
          this.offers = object.content
          this.offersTotal = object.totalElements
        } else {
          this.errorOffers = true
        }
      } catch (e) {
        this.errorOffers = true
      } finally {
        this.loadingOffers = false
      }
    },
    async getTransactions(page, size) {
      this.loadingTx = true
      this.errorTx = false
      try {
        const response = await this.$apiFetch(
          `${this.apiBase()}/private/api/client/end-user/${this.userId}/transactions?page=${page}&size=${size}`,
          { method: 'GET', headers: this.authHeaders() }
        )
        if (response.ok) {
          const object = await response.json()
          this.transactions = object.content
          this.txTotal = object.totalElements
          this.txLoaded = true
        } else {
          this.errorTx = true
        }
      } catch (e) {
        this.errorTx = true
      } finally {
        this.loadingTx = false
      }
    },
    formatRecommendationType(value) {
      return value ? String(value).replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase()) : '—'
    },
    recommendationTransaction(data) {
      if (!data) return '—'
      const amount = data.transactionAmount || data.amount
      if (typeof amount === 'number') return `${data.currency || 'GBP'} ${amount.toFixed(2)}`
      if (amount && typeof amount.amount === 'number') return `${amount.currency || 'GBP'} ${amount.amount.toFixed(2)}`
      return data.transactionId || data.id || '—'
    },
    recommendationCashback(data) {
      const amount = data?.endUserCashbackReceivable ?? data?.cashbackAmount
      if (amount == null) return '—'
      const currency = data?.currency || 'GBP'
      try { return new Intl.NumberFormat('en-GB', { style: 'currency', currency }).format(Number(amount)) }
      catch { return `${currency} ${amount}` }
    },
    async getRecommendations(page, size) {
      this.loadingRecommendations = true
      this.errorRecommendations = false
      try {
        const response = await this.$apiFetch(
          `${this.apiBase()}/private/api/client/recommendations/user/${encodeURIComponent(this.userId)}?page=${page}&size=${size}`,
          { method: 'GET', headers: this.authHeaders() }
        )
        if (!response.ok) throw new Error(`Recommendations request failed: ${response.status}`)
        const object = await response.json()
        this.recommendations = object.content || []
        this.recommendationsTotal = object.totalElements ?? this.recommendations.length
        this.recommendationsLoaded = true
      } catch (e) {
        this.errorRecommendations = true
      } finally {
        this.loadingRecommendations = false
      }
    },
    async getAdvertisingEvents(page, size) {
      this.loadingAd = true
      this.errorAd = false
      try {
        const response = await this.$apiFetch(
          `${this.apiBase()}/private/api/client/end-user/${this.userId}/advertising-events?page=${page}&size=${size}`,
          { method: 'GET', headers: this.authHeaders() }
        )
        if (response.ok) {
          const object = await response.json()
          this.adEvents = object.content
          this.adTotal = object.totalElements
          this.adLoaded = true
        } else {
          this.errorAd = true
        }
      } catch (e) {
        this.errorAd = true
      } finally {
        this.loadingAd = false
      }
    },
  },
  watch: {
    ticketOptions: {
      deep: true,
      handler(val) {
        this.getTickets(val.page - 1, val.rowsPerPage)
      },
    },
    offerOptions: {
      deep: true,
      handler(val) {
        this.getOffers(val.page - 1, val.rowsPerPage)
      },
    },
    txOptions: {
      deep: true,
      handler(val) {
        if (this.txLoaded) this.getTransactions(val.page - 1, val.rowsPerPage)
      },
    },
    recommendationOptions: {
      deep: true,
      handler(val) {
        if (this.recommendationsLoaded) this.getRecommendations(val.page - 1, val.rowsPerPage)
      },
    },
    adOptions: {
      deep: true,
      handler(val) {
        if (this.adLoaded) this.getAdvertisingEvents(val.page - 1, val.rowsPerPage)
      },
    },
  },
}
</script>
